import { EventEmitter } from 'events';
import { WsProvider, ApiPromise, HttpProvider } from '@polkadot/api';

const options = {
  edgeware: {},
  usetech: {},
  basilisk: {}
};
const regexes = {
  edgeware: /edgewa/,
  usetech: /usetech/,
  basilisk: /basilisk/
};
const getApiOptions = (apiUrl) => {
  if (regexes.basilisk.test(apiUrl)) {
    return { ...options.basilisk };
  }
  if (regexes.edgeware.test(apiUrl)) {
    return { ...options.edgeware };
  }
  if (regexes.usetech.test(apiUrl)) {
    return { ...options.usetech };
  }
  return {};
};

const _Api = class extends EventEmitter {
  /**
   * getInstance
   * @returns Api Instance
   */
  static getInstance() {
    return _Api._instance;
  }
  /**
   * connect
   * @requires apiUrl: string
   * @returns instance of polkadot-js/api instance
   */
  async connect(apiUrl, overrideOptions) {
    if (!apiUrl || typeof apiUrl !== "string") {
      throw new TypeError(
        `[VUE API] ERR: Unable to init api with apiUrl ${apiUrl}`
      );
    }
    try {
      await this.disconnect();
      const provider = new WsProvider(apiUrl);
      const options = overrideOptions ?? getApiOptions(apiUrl);
      const apiPromise = await ApiPromise.create({ provider, ...options });
      apiPromise.once("connected", () => this._emit("connected", apiUrl)).once("ready", () => this._emit("ready", apiUrl)).on("disconnected", () => this._emit("disconnected", apiUrl));
      this.setApi(apiPromise);
      this._emit("connect", apiPromise);
    } catch (err) {
      this._emit("error", err);
      throw err;
    }
    this.setUrl(apiUrl);
    return this._api;
  }
  /**
   * disconnect
   */
  async disconnect() {
    if (this._api) {
      await this._api.disconnect();
      this.setUrl(null);
    }
  }
  setApi(api) {
    this._api = api;
  }
  setUrl(apiUrl) {
    this._apiUrl = apiUrl;
  }
  get api() {
    return this._api;
  }
  /**
   * tryEmit
   *
   */
  _emit(message = "event", payload) {
    this.emit(message, payload);
  }
};
let Api$1 = _Api;
Api$1._instance = new _Api();

function asHttpProvider(url) {
  return new HttpProvider(url);
}
function asWsProvider(url) {
  return new WsProvider(url);
}

const WS_PREFIX = /^wss?:\/\//;
const HTTP_PREFIX = /^https?:\/\//;
const DETATCH_IN = 2e3;

class InstantApi extends ApiPromise {
  // private _initiatedAt: number
  constructor(apiUrl, overrideOptions) {
    const provider = asWsProvider(apiUrl);
    const options = overrideOptions ?? getApiOptions(apiUrl);
    super({ provider, ...options, throwOnConnect: true });
    this.setUrl(apiUrl);
    this.setSpawn();
    this.once("error", () => {
      console.warn(
        "[KODADOT::SUBAPI] WARN: Unable to init api with apiUrl",
        apiUrl
      );
    });
    this.once("disconnected", () => {
      console.warn(
        "[KODADOT::SUBAPI] LOG: Api disconnected",
        apiUrl
      );
      this.detach();
      this.setSpawn(false);
    });
  }
  getInstance() {
    return this.isReadyOrError.then(
      (api) => api,
      () => {
        throw new EvalError(`[KODADOT::SUBAPI] Error: cannot get instance at ${this._apiUrl}`);
      }
    );
  }
  get instance() {
    return this.isReadyOrError;
  }
  get isSpawned() {
    return this._isSpawned;
  }
  async revive() {
    if (this.isDead) {
      console.warn("[KODADOT::SUBAPI] WARN: Reviving api");
      await this.clone();
    } else {
      console.log("[KODADOT::SUBAPI] LOG: Api is already connected");
    }
  }
  detach() {
    console.warn(`[KODADOT::SUBAPI] WARN: Detaching api in ${DETATCH_IN} ms`);
    setTimeout(() => {
      this.disconnect();
    }, DETATCH_IN);
  }
  setSpawn(spawn = true) {
    this._isSpawned = spawn;
  }
  setUrl(apiUrl) {
    this._apiUrl = apiUrl;
  }
  get isDead() {
    return this.isConnected === false;
  }
  get url() {
    return this._apiUrl;
  }
}
const Api = InstantApi;

const connectionMap = /* @__PURE__ */ new Map();
class ApiFactory {
  useApi(prefixOrUrl) {
    if (this.isAlreadyConnected(prefixOrUrl)) {
      console.log(`[KODADOT::SUBAPI] LOG: Api already connected at ${prefixOrUrl}`);
      const api = connectionMap.get(prefixOrUrl);
      console.log("api.isConnected?", api.isConnected ? "\u{1F49A}" : "\u{1F480}");
      if (api.isSpawned) {
        return api;
      }
    }
    return this.initConnection(prefixOrUrl);
  }
  useApiInstance(prefixOrUrl) {
    const api = this.useApi(prefixOrUrl);
    return api.getInstance();
  }
  byeApi(prefixOrUrl) {
    if (this.isAlreadyConnected(prefixOrUrl)) {
      const api = connectionMap.get(prefixOrUrl);
      api.disconnect();
    } else {
      console.warn(`[KODADOT::SUBAPI] WARN: Cannot bye api at ${prefixOrUrl}`);
    }
  }
  isAlreadyConnected(prefixOrUrl) {
    return connectionMap.has(prefixOrUrl);
  }
  initConnection(prefixOrUrl) {
    const api = new Api(prefixOrUrl);
    connectionMap.set(prefixOrUrl, api);
    return api;
  }
}
const factory = new ApiFactory();
const ApiFactory$1 = factory;

async function onApiConnect(prefixOrUrl, callback, onError) {
  try {
    const api = await ApiFactory$1.useApiInstance(prefixOrUrl);
    callback(api);
  } catch (err) {
    if (onError) {
      onError(err);
    } else {
      console.error(err);
    }
  }
}

function useApi(prefixOrUrl) {
  const provider = asHttpProvider(getUrl(prefixOrUrl));
  return ApiPromise.create({ provider });
}
function getUrl(prefixOrUrl) {
  if (isWebSocketUrl(prefixOrUrl)) {
    throw new ReferenceError("[KODADOT::SUBAPI] ERR: WebSocket url is not supported");
  }
  if (isHttptUrl(prefixOrUrl)) {
    return prefixOrUrl;
  }
  return getUrlByPrefix(prefixOrUrl);
}
function isWebSocketUrl(url) {
  return WS_PREFIX.test(url);
}
function isHttptUrl(url) {
  return HTTP_PREFIX.test(url);
}
function getUrlByPrefix(maybePrefix) {
  return "http://" + maybePrefix;
}

const asUtilityBatch = (api, calls) => {
  return api.tx.utility.batchAll(calls);
};
const asBalanceTransfer = (api, to, amount) => {
  return api.tx.balances.transfer(to, amount);
};

const identityOf = (api, address) => {
  return api.query.identity.identityOf(address);
};
const balanceOf = (api, address) => {
  return api.derive.balances.all(address).then(({ availableBalance }) => availableBalance.toString());
};
const currentBlock = (api) => {
  return api.query.system.number().then((value) => value.toString());
};

export { ApiFactory$1 as ApiFactory, Api as InstantApi, asBalanceTransfer, asUtilityBatch, balanceOf, currentBlock, Api$1 as default, identityOf, onApiConnect, useApi };
